import React from 'react';
import { Badge } from 'react-bootstrap';
import { Clock, Building, RefreshCw, PaintRoller, Coins } from 'lucide-react';
import { UpcomingMaintenanceItem } from '../../../hooks/management/useUpcomingMaintenance';
import { useBuildComponentTypes } from '../../../contexts/BuildComponentTypesContext';
import { MaintenanceIntervalType } from '../../../types/Enums';
import { formatDateForDisplay, getDaysUntilDue, formatDateOnlyForStorage } from '../../../Helpers/DateUtils';

interface MaintenanceCardProps {
    item: UpcomingMaintenanceItem;
    onClick?: () => void;
    isSelected?: boolean;
}

/**
 * Get urgency badge based on days until due
 */
function getUrgencyBadge(daysUntil: number): { variant: string; label: string } | null {
    if (daysUntil <= 0) return null;
    if (daysUntil <= 30) return { variant: 'warning', label: 'Inom 30 dagar' };
    if (daysUntil <= 90) return { variant: 'info', label: 'Inom 3 mån' };
    return null;
}

/**
 * Format interval for display (e.g., "Var 2:a år")
 */
function getOrdinalSuffix(n: number): string {
    return (n % 10 === 2 && n % 100 !== 12) ? ':a' : ':e';
}

function formatInterval(intervalType: MaintenanceIntervalType, intervalValue: number): string {
    const typeLabels: Record<MaintenanceIntervalType, { singular: string; neuter: boolean }> = {
        [MaintenanceIntervalType.days]: { singular: 'dag', neuter: false },
        [MaintenanceIntervalType.weeks]: { singular: 'vecka', neuter: false },
        [MaintenanceIntervalType.months]: { singular: 'månad', neuter: false },
        [MaintenanceIntervalType.years]: { singular: 'år', neuter: true }
    };

    const label = typeLabels[intervalType] || { singular: 'år', neuter: true };

    if (intervalValue === 1) {
        return `Varje ${label.singular}`;
    }
    if (intervalValue === 2) {
        return `${label.neuter ? 'Vartannat' : 'Varannan'} ${label.singular}`;
    }
    const prefix = label.neuter ? 'Vart' : 'Var';
    return `${prefix} ${intervalValue}${getOrdinalSuffix(intervalValue)} ${label.singular}`;
}

/**
 * Card displaying an upcoming maintenance activity
 */
const MaintenanceCard: React.FC<MaintenanceCardProps> = ({
    item,
    onClick,
    isSelected
}) => {
    const { getTemplateById, getTranslation } = useBuildComponentTypes();

    // Get proper template name from config (fallback to formatted templateId)
    const template = getTemplateById(item.templateId);
    const activityName = template?.name || item.activityName;

    // Get translated component type name
    const componentTypeName = item.componentType
        ? getTranslation(item.componentType)
        : 'Okänd komponent';

    const daysUntil = getDaysUntilDue(item.nextDate);
    const urgency = getUrgencyBadge(daysUntil);
    const isOverdue = daysUntil <= 0;
    const today = formatDateOnlyForStorage(new Date());
    const isActivelySnoozed = !!item.snoozedUntil && item.snoozedUntil > today;

    const handleClick = () => {
        onClick?.();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            onClick?.();
        }
    };

    return (
        <div
            className={`management-item ${isOverdue ? 'management-item--overdue' : ''} ${isSelected ? 'management-item--selected' : ''}`}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={handleKeyDown}
        >
            <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1 me-2">
                    <div className="d-flex align-items-center gap-1 mb-1">
                        <Badge bg="" className="badge-underhall d-inline-flex align-items-center gap-1">
                            <PaintRoller size={10} />
                            Underhåll
                        </Badge>
                    </div>
                    <div className="fw-medium">{activityName}</div>
                    <div className="small text-muted">
                        {componentTypeName}
                        {item.componentLabel && (
                            <span className="ms-1">({item.componentLabel})</span>
                        )}
                    </div>
                </div>
                {isActivelySnoozed ? (
                    <Badge bg="secondary" className="badge-label">
                        Snoozad
                    </Badge>
                ) : urgency && (
                    <Badge bg="" className="badge-overdue">
                        {urgency.label}
                    </Badge>
                )}
            </div>

            <div className="mt-2 d-flex align-items-center gap-2 small text-muted">
                <Building size={12} />
                <span>{item.buildingName}</span>
            </div>

            <div className="mt-1 d-flex align-items-center gap-2">
                <small className={`d-flex align-items-center gap-1 ${isOverdue ? 'text-danger' : 'text-muted'}`}>
                    <Clock size={12} />
                    {formatDateForDisplay(item.nextDate)}
                </small>
                <small className="d-flex align-items-center gap-1 text-muted">
                    <RefreshCw size={10} />
                    {formatInterval(item.intervalType, item.intervalValue)}
                </small>
                {item.cost !== undefined && item.cost > 0 && (
                    <small className="d-flex align-items-center gap-1 text-muted">
                        <Coins size={12} />
                        {item.cost.toLocaleString('sv-SE')} kr
                    </small>
                )}
            </div>
        </div>
    );
};

export default MaintenanceCard;
